/**
 * Capacitor Native Service
 * ========================
 * 
 * Servicio para manejar todas las funcionalidades nativas de la app móvil.
 * Compatible con iOS y Android a través de Capacitor.
 */

import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar } from '@capacitor/status-bar';

// ==================== DETECCIÓN DE PLATAFORMA ====================

export const isNative = () => Capacitor.isNativePlatform();
export const isIOS = () => Capacitor.getPlatform() === 'ios';
export const isAndroid = () => Capacitor.getPlatform() === 'android';
export const isWeb = () => Capacitor.getPlatform() === 'web';

// ==================== INICIALIZACIÓN ====================

export const initializeApp = async () => {
  if (!isNative()) return;

  try {
    // Ocultar splash screen después de cargar
    await SplashScreen.hide();

    // Configurar status bar
    if (isAndroid()) {
      await StatusBar.setBackgroundColor({ color: '#2563eb' });
    }

    // Configurar listener de back button (Android)
    setupBackButtonListener();

    // Configurar deep links
    setupDeepLinkListener();

    // App nativa inicializada correctamente
  } catch (_error) {
    // Native app initialization failed
  }
};

// ==================== SPLASH SCREEN ====================

export const showSplashScreen = async () => {
  if (!isNative()) return;
  await SplashScreen.show({
    autoHide: false,
    showSpinner: true,
  });
};

export const hideSplashScreen = async () => {
  if (!isNative()) return;
  await SplashScreen.hide();
};

// ==================== STATUS BAR ====================

export const setStatusBarColor = async (color, _darkIcons = false) => {
  if (!isNative()) return;
  
  if (isAndroid()) {
    await StatusBar.setBackgroundColor({ color });
  }
};

// ==================== HAPTICS (VIBRACIÓN) - Simplified ====================

export const hapticImpact = async (style = 'medium') => {
  if (!isNative()) return;
  // Haptics require additional plugin, simplified for now
  if (navigator.vibrate) {
    const durations = {
      light: 10,
      medium: 30,
      heavy: 50,
    };
    navigator.vibrate(durations[style] || 30);
  }
};

export const hapticNotification = async (type = 'success') => {
  if (!isNative()) return;
  if (navigator.vibrate) {
    const patterns = {
      success: [30, 50, 30],
      warning: [50, 30, 50],
      error: [100],
    };
    navigator.vibrate(patterns[type] || [30]);
  }
};

export const hapticVibrate = async () => {
  if (!isNative()) return;
  if (navigator.vibrate) {
    navigator.vibrate(50);
  }
};

// ==================== SHARE ====================

export const shareContent = async (title, text, url, _dialogTitle) => {
  try {
    if (navigator.share) {
      await navigator.share({
        title,
        text,
        url,
      });
      return true;
    }
    return false;
  } catch (_error) {
    return false;
  }
};

// Sin «Top X %» (ola 2 · O2.4): no hay cohorte con la que comparar; se comparte la nota tal cual.
// `_percentil` se conserva en la firma por compatibilidad con los llamadores, pero no se usa.
export const shareSimulacroResult = async (simulacroName, nota, _percentil) => {
  const text = `🎓 He completado el simulacro "${simulacroName}" en TuPlazaIA con una nota de ${nota}. ¡Preparándome para las oposiciones!`;
  const url = 'https://tuplazaia.com';
  
  return shareContent('Mi resultado en TuPlazaIA', text, url);
};

// ==================== BROWSER ====================

export const openExternalUrl = async (url) => {
  window.open(url, '_blank');
};

export const closeExternalBrowser = async () => {
  // Not applicable in web
};

// ==================== APP LIFECYCLE ====================

const setupBackButtonListener = () => {
  if (!isAndroid()) return;

  App.addListener('backButton', ({ canGoBack }) => {
    if (canGoBack) {
      window.history.back();
    } else {
      App.minimizeApp();
    }
  });
};

const setupDeepLinkListener = () => {
  if (!isNative()) return;

  App.addListener('appUrlOpen', (event) => {
    const url = new URL(event.url);
    const path = url.pathname;
    
    if (path) {
      window.location.href = path;
    }
  });
};

export const getAppInfo = async () => {
  if (!isNative()) {
    return {
      name: 'TuPlazaIA',
      version: '1.0.0',
      build: '1',
      platform: 'web',
    };
  }

  const info = await App.getInfo();
  return {
    name: info.name,
    version: info.version,
    build: info.build,
    platform: Capacitor.getPlatform(),
  };
};

export const exitApp = async () => {
  if (!isNative()) return;
  await App.exitApp();
};

// ==================== PUSH NOTIFICATIONS (Simplified) ====================

export const requestPushPermission = async () => {
  if (!('Notification' in window)) return false;
  
  try {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  } catch (_error) {
    return false;
  }
};

export const setupPushNotifications = (_onRegistration, _onNotification, _onAction) => {
  // Simplified for web - native push requires additional setup
};

// ==================== LOCAL NOTIFICATIONS (Web fallback) ====================

export const scheduleLocalNotification = async (id, title, body, scheduleAt, _extra) => {
  if (!('Notification' in window)) return;
  
  if (Notification.permission === 'granted') {
    const delay = scheduleAt.getTime() - Date.now();
    if (delay > 0) {
      setTimeout(() => {
        new Notification(title, { body, tag: String(id) });
      }, delay);
    }
  }
};

export const cancelLocalNotification = async (_id) => {
  // Not easily cancelable in web
};

export const cancelAllLocalNotifications = async () => {
  // Not easily cancelable in web
};

// ==================== STUDY REMINDERS ====================

export const scheduleStudyReminder = async (hora, tema, _diasSemana = [1, 2, 3, 4, 5]) => {
  if (!isNative()) return;
};

export const cancelStudyReminders = async () => {
  if (!isNative()) return;
};

export const notifyNewConvocatoria = async (titulo, organismo, plazas) => {
  const body = `${organismo} - ${plazas} plazas`;
  
  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification(`🔔 Nueva convocatoria: ${titulo.substring(0, 50)}...`, { body });
  }
};

// ==================== KEYBOARD ====================

export const hideKeyboard = async () => {
  if (document.activeElement) {
    document.activeElement.blur();
  }
};

// ==================== EXPORT DEFAULT ====================

export default {
  // Detection
  isNative,
  isIOS,
  isAndroid,
  isWeb,
  
  // Init
  initializeApp,
  
  // Splash
  showSplashScreen,
  hideSplashScreen,
  
  // Status Bar
  setStatusBarColor,
  
  // Keyboard
  hideKeyboard,
  
  // Push Notifications
  requestPushPermission,
  setupPushNotifications,
  
  // Local Notifications
  scheduleLocalNotification,
  cancelLocalNotification,
  cancelAllLocalNotifications,
  
  // Haptics
  hapticImpact,
  hapticNotification,
  hapticVibrate,
  
  // Share
  shareContent,
  shareSimulacroResult,
  
  // Browser
  openExternalUrl,
  closeExternalBrowser,
  
  // App
  getAppInfo,
  exitApp,
  
  // Study
  scheduleStudyReminder,
  cancelStudyReminders,
  notifyNewConvocatoria,
};
